import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  isPlayerSupported,
  PlayerError,
  PlayerEventType,
  PlayerState,
  registerIVSTech,
  VideoJSIVSTech,
} from 'amazon-ivs-player'
import '@css/common/VideoPlayer.scss'

import { getStaticBase } from '~/api/ServerApi'
import Hls from 'hls.js'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import { logLivestreamEvent } from '~/common/EventLogger'

function supportsWebWorkers(): boolean {
  return typeof Worker !== 'undefined'
}

export const HlsVideoPlayer = ({
  src,
  className = 'video',
  ...props
}: {
  src: string
  className: string
  muted?: boolean
}) => {
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null)

  const registerHls = useCallback(
    (videoRef: HTMLVideoElement) => {
      setVideoElement(videoRef)
    },
    [setVideoElement]
  )

  useEffect(() => {
    const videoRef = videoElement
    if (!videoRef) return
    if (Hls.isSupported()) {
      const hls = new Hls({
        debug: true,
      })
      // hls.log = true
      hls.loadSource(src)
      hls.attachMedia(videoRef)
      hls.on(Hls.Events.ERROR, (code, err) => {
        console.log('hlserror', code, err)
      })
      const loadedmetadatahandler = () => {
        void videoRef.play()
      }
      videoRef.addEventListener('loadedmetadata', loadedmetadatahandler)
      return () => {
        hls.destroy()
      }
    } else {
      console.log('load')
    }
  }, [videoElement, src])
  return (
    <video
      disablePictureInPicture
      muted={props.muted ?? false}
      ref={r => (r && registerHls(r)) || undefined}
      className={className}
      controls
      {...props}
      src={src}
    />
  )
}

const IvsVideoPlayer = ({
  src,
  className = 'video',
  ...props
}: {
  src: string
  className: string
  muted?: boolean
}) => {
  const videoElement = useRef<HTMLVideoElement | null>(null)
  const registerIvsRef = useCallback(
    (videoRef: HTMLVideoElement) => {
      // React will call this on every rerender so we need to avoid reinitializing
      if (videoElement.current) {
        return
      }
      // Videojs removes the original video element from the parent, so we need to clone it so the original
      // is still there for react to clean up when the component unmounts.
      const newNode = videoRef.cloneNode(true) as HTMLVideoElement
      videoRef.parentNode?.append(newNode)
      videoRef.hidden = true
      newNode.hidden = false
      videoElement.current = newNode
    },
    [videoElement]
  )
  const muted = props.muted ?? false
  useEffect(() => {
    if (!videoElement.current) return
    registerIVSTech(videojs, {
      wasmWorker: `${getStaticBase()}/ivs/amazon-ivs-wasmworker.min.js`,
      wasmBinary: `${getStaticBase()}/ivs/amazon-ivs-wasmworker.min.wasm`,
    })

    const vjsplayer = videojs(
      videoElement.current,
      {
        techOrder: ['AmazonIVS'],
        controlBar: {
          pictureInPictureToggle: false,
          textTrackSettings: false,
        },
      },
      () => {
        const ivsPlayer = (vjsplayer as unknown as VideoJSIVSTech).getIVSPlayer()
        ivsPlayer.addEventListener(PlayerState.PLAYING, () => {
          console.log('Player State - PLAYING')
          setTimeout(() => {
            console.log(`This stream is ${ivsPlayer.isLiveLowLatency() ? '' : 'not '}playing in ultra low latency mode`)
            console.log(`Stream Latency: ${ivsPlayer.getLiveLatency()}s`)
          }, 5000)
        })

        ivsPlayer.addEventListener(PlayerEventType.ERROR, (payload: PlayerError) => {
          console.warn('Player Event - ERROR', payload.type, payload.source)
        })

        ivsPlayer.setAutoplay(true)
        ivsPlayer.setMuted(muted)
        vjsplayer.src(src)
      }
    )
    logLivestreamEvent(true)
    return () => {
      logLivestreamEvent(false)
      try {
        vjsplayer.dispose()
        videoElement.current?.remove()
        videoElement.current = null
      } catch (error) {
        console.error('error disposing vjsplayer', error)
      }
    }
  }, [src, videoElement, muted])

  return (
    <video
      disablePictureInPicture
      muted={props.muted ?? false}
      ref={r => (r && registerIvsRef(r)) || undefined}
      className={`${className} video-js vjs-default-skin vjs-fluid`}
      controls
      {...props}
      src={src}
    />
  )
}

export const VideoPlayer = supportsWebWorkers() && isPlayerSupported ? IvsVideoPlayer : HlsVideoPlayer
